/**
 * InvoicesSettings — Faktury tab. Lists mirrored Stripe invoices with PDF
 * download links. Payment-method management lives on the Subscription tab.
 *
 * ADMIN role only.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { Decimal } from 'decimal.js';
import { formatMoney } from '@insio/decimal';
import type { CurrencyCode } from '@insio/shared-types';
import { Badge, Button, Paper, SimpleTable, useLocale } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import {
  GetStripeInvoicesDocument,
  type GetStripeInvoicesQuery,
  type StripeInvoiceStatus
} from '@/graphql';
import { useFormatShortDate } from '@/hooks/use-date-formatters';
import { useToastHelpers } from '@/hooks/use-toast-helpers';

type StripeInvoice = GetStripeInvoicesQuery['stripeInvoices'][number];

const PAGE_SIZE = 12;

const STATUS_BADGE_COLOR: Record<StripeInvoiceStatus, 'success' | 'warning' | 'error' | 'info'> = {
  PAID: 'success',
  OPEN: 'warning',
  VOID: 'info',
  UNCOLLECTIBLE: 'error'
};

export function InvoicesSettings() {
  const t = useFeatureTranslation('systemSettings.invoices');
  const locale = useLocale();
  const formatShortDate = useFormatShortDate();
  const apolloClient = useApolloClient();
  const { showError } = useToastHelpers();

  const [extraPages, setExtraPages] = useState<StripeInvoice[]>([]);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, loading } = useQuery(GetStripeInvoicesDocument, {
    variables: { input: { limit: PAGE_SIZE } },
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    const firstPage = data?.stripeInvoices;
    if (firstPage && firstPage.length < PAGE_SIZE) {
      setReachedEnd(true);
    }
  }, [data]);

  const allInvoices = useMemo(
    () => [...(data?.stripeInvoices ?? []), ...extraPages],
    [data, extraPages]
  );

  const handleLoadOlder = async () => {
    if (allInvoices.length === 0) return;
    setLoadingMore(true);
    try {
      const last = allInvoices[allInvoices.length - 1];
      const result = await apolloClient.query({
        query: GetStripeInvoicesDocument,
        variables: { input: { limit: PAGE_SIZE, before: last.invoiceCreatedAt } },
        fetchPolicy: 'network-only'
      });
      const more = result.data?.stripeInvoices ?? [];
      setExtraPages(prev => [...prev, ...more]);
      if (more.length < PAGE_SIZE) setReachedEnd(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load older invoices', error);
      showError(t('loadOlderError'));
    } finally {
      setLoadingMore(false);
    }
  };

  const formatAmount = (amount: Decimal, currency: string): string =>
    formatMoney(amount, {
      currencyCode: currency.toUpperCase() as CurrencyCode,
      locale
    });

  const hasInvoices = allInvoices.length > 0;
  const initialLoad = loading && !data;

  const headers = [
    t('table.number'),
    t('table.date'),
    t('table.status'),
    t('table.period'),
    t('table.plan'),
    <span key='amount-header' className='flex justify-end'>
      {t('table.amount')}
    </span>,
    <span key='pdf-header' className='flex justify-center'>
      {t('table.pdf')}
    </span>
  ];

  return (
    <div className='space-y-lg'>
      <Paper showDivider={false} padding='none'>
        {initialLoad ? (
          <div className='py-xl text-center'>
            <p className='text-sm text-foreground-muted'>{t('loading')}</p>
          </div>
        ) : !hasInvoices ? (
          <div className='py-2xl text-center flex flex-col items-center gap-sm'>
            <h3 className='text-base font-semibold text-foreground-base'>{t('empty.title')}</h3>
            <p className='text-sm text-foreground-muted max-w-md'>{t('empty.description')}</p>
          </div>
        ) : (
          <>
            <SimpleTable<StripeInvoice>
              headers={headers}
              data={allInvoices}
              scrollable
              renderRow={invoice => [
                <span key='number' className='text-xs md:text-sm font-medium text-foreground-base'>
                  {invoice.number}
                </span>,
                <span key='date' className='text-xs md:text-sm text-foreground-base'>
                  {formatShortDate(invoice.invoiceCreatedAt)}
                </span>,
                <Badge key='status' color={STATUS_BADGE_COLOR[invoice.status]} size='sm'>
                  {t(`status.${invoice.status.toLowerCase()}`)}
                </Badge>,
                <span key='period' className='text-xs md:text-sm text-foreground-base'>
                  {`${formatShortDate(invoice.periodStart)} – ${formatShortDate(invoice.periodEnd)}`}
                </span>,
                <span key='plan' className='text-xs md:text-sm text-foreground-base'>
                  {invoice.planName ?? '—'}
                </span>,
                <span
                  key='amount'
                  className='text-xs md:text-sm font-medium text-foreground-base flex justify-end'
                >
                  {formatAmount(invoice.amountPaid, invoice.currency)}
                </span>,
                <div key='pdf' className='flex justify-center'>
                  {invoice.invoicePdf || invoice.hostedInvoiceUrl ? (
                    <Button
                      variant='ghost'
                      size='sm'
                      icon='download'
                      onClick={() => {
                        const url = invoice.invoicePdf ?? invoice.hostedInvoiceUrl;
                        if (url) window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      {t('table.download')}
                    </Button>
                  ) : null}
                </div>
              ]}
            />
            {!reachedEnd && (
              <div className='flex justify-center p-md'>
                <Button
                  variant='ghost'
                  disabled={loadingMore}
                  onClick={() => void handleLoadOlder()}
                >
                  {loadingMore ? t('loading') : t('loadOlder')}
                </Button>
              </div>
            )}
          </>
        )}
      </Paper>
    </div>
  );
}
